import { useState, useEffect, useCallback } from "react";
import { marginAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Loader2, TrendingUp, TrendingDown, Euro, Percent, FileText, Package, Zap, Battery, Sun, Box, Shield, Wrench, Users } from "lucide-react";

function MarginIcon({ value }) {
  if (value >= 0) {
    return <TrendingUp className="h-5 w-5 text-green-500" />;
  }
  return <TrendingDown className="h-5 w-5 text-red-500" />;
}

function MarginBadge({ percent }) {
  const formatted = new Intl.NumberFormat("de-DE", { 
    style: "percent", 
    minimumFractionDigits: 1, 
    maximumFractionDigits: 1 
  }).format((percent || 0) / 100);
  
  let variant = "destructive";
  if (percent >= 20) {
    variant = "default";
  } else if (percent >= 10) {
    variant = "secondary";
  }
  
  return <Badge variant={variant}>{formatted}</Badge>;
}

export default function MarginOverviewPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("signed");
  const [selectedMargin, setSelectedMargin] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const isAdmin = user?.role === "superadmin" || user?.role === "admin";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await marginAPI.getOverview(statusFilter);
      setData(response.data);
    } catch (error) {
      console.error("Failed to load margin data:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, loadData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat("de-DE", { 
      style: "percent", 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    }).format((value || 0) / 100);
  };

  const getMarginColorClass = (value) => {
    return value >= 0 ? "text-green-600" : "text-red-600";
  };

  const handleRowClick = (margin) => {
    setSelectedMargin(margin);
    setDetailDialogOpen(true);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="margin-no-permission">
        <p className="text-muted-foreground">Keine Berechtigung</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="margin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="margin-overview-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Margenübersicht</h1>
          <p className="text-muted-foreground">Analyse der Projektmargen</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]" data-testid="margin-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="signed">Unterschriebene Angebote</SelectItem>
            <SelectItem value="sent">Versendete Angebote</SelectItem>
            <SelectItem value="draft">Entwürfe</SelectItem>
            <SelectItem value="all">Alle Angebote</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data?.summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card data-testid="summary-count-card">
            <CardHeader className="pb-2">
              <CardDescription>Anzahl Angebote</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{data.summary.count}</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="summary-revenue-card">
            <CardHeader className="pb-2">
              <CardDescription>Gesamtumsatz</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Euro className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{formatCurrency(data.summary.total_sale)}</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="summary-margin-card">
            <CardHeader className="pb-2">
              <CardDescription>Gesamtmarge</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <MarginIcon value={data.summary.total_margin} />
                <span className={`text-2xl font-bold ${getMarginColorClass(data.summary.total_margin)}`}>
                  {formatCurrency(data.summary.total_margin)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="summary-avg-margin-card">
            <CardHeader className="pb-2">
              <CardDescription>Durchschnitt Marge</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-muted-foreground" />
                <span className={`text-2xl font-bold ${getMarginColorClass(data.summary.avg_margin_percent)}`}>
                  {formatPercent(data.summary.avg_margin_percent)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card data-testid="margin-table-card">
        <CardHeader>
          <CardTitle>Margen pro Angebot</CardTitle>
          <CardDescription>Detaillierte Aufschlüsselung der Projektmargen</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.offers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Angebot</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead className="text-right">Module</TableHead>
                  <TableHead className="text-right">VK</TableHead>
                  <TableHead className="text-right">EK</TableHead>
                  <TableHead className="text-right">Montage</TableHead>
                  <TableHead className="text-right">Provision</TableHead>
                  <TableHead className="text-right">Marge EUR</TableHead>
                  <TableHead className="text-right">Marge %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.offers.map((margin) => (
                  <TableRow 
                    key={margin.offer_id} 
                    data-testid={`margin-row-${margin.offer_id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(margin)}
                  >
                    <TableCell className="font-medium">{margin.offer_number}</TableCell>
                    <TableCell>{margin.customer_name}</TableCell>
                    <TableCell className="text-right">{margin.module_count}</TableCell>
                    <TableCell className="text-right">{formatCurrency(margin.sale_price)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(margin.total_purchase_cost)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(margin.total_installation_cost)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(margin.commission || 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${getMarginColorClass(margin.margin_amount)}`}>
                        {formatCurrency(margin.margin_amount)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <MarginBadge percent={margin.margin_percent} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-testid="margin-empty-state">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine Angebote gefunden</p>
              <p className="text-sm">Wählen Sie einen anderen Status-Filter</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Margendetails - {selectedMargin?.offer_number}
            </DialogTitle>
            <DialogDescription>
              {selectedMargin?.customer_name} • {selectedMargin?.module_count} Module
            </DialogDescription>
          </DialogHeader>

          {selectedMargin && (
            <div className="space-y-6">
              {/* Verkaufspreis */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Euro className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Verkaufspreis (VK)</span>
                  </div>
                  <span className="text-xl font-bold text-green-700">
                    {formatCurrency(selectedMargin.sale_price)}
                  </span>
                </div>
              </div>

              {/* Einkaufskosten */}
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="h-4 w-4" />
                  EINKAUFSKOSTEN (EK)
                </h4>
                <div className="border rounded-lg divide-y">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <Sun className="h-4 w-4 text-yellow-500" />
                      <span>Module ({selectedMargin.module_count}×)</span>
                    </div>
                    <span className="font-mono">{formatCurrency(selectedMargin.module_purchase_cost)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <Battery className="h-4 w-4 text-blue-500" />
                      <span>Speicher</span>
                    </div>
                    <span className="font-mono">{formatCurrency(selectedMargin.storage_purchase_cost)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-500" />
                      <span>Wechselrichter</span>
                    </div>
                    <span className="font-mono">{formatCurrency(selectedMargin.inverter_purchase_cost)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-gray-500" />
                      <span>Erdung</span>
                    </div>
                    <span className="font-mono">{formatCurrency(selectedMargin.grounding_purchase_cost)}</span>
                  </div>
                  {selectedMargin.meter_cabinet_purchase_cost > 0 && (
                    <div className="flex items-center justify-between p-3 bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Box className="h-4 w-4 text-purple-500" />
                        <span>Zählerschrank</span>
                      </div>
                      <span className="font-mono">{formatCurrency(selectedMargin.meter_cabinet_purchase_cost)}</span>
                    </div>
                  )}
                  {selectedMargin.wallbox_purchase_cost > 0 && (
                    <div className="flex items-center justify-between p-3 bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-green-500" />
                        <span>Wallbox</span>
                      </div>
                      <span className="font-mono">{formatCurrency(selectedMargin.wallbox_purchase_cost)}</span>
                    </div>
                  )}
                  {selectedMargin.other_purchase_costs > 0 && (
                    <div className="flex items-center justify-between p-3 bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-600" />
                        <span>Backup (Notstrom)</span>
                      </div>
                      <span className="font-mono">{formatCurrency(selectedMargin.other_purchase_costs)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 bg-muted font-medium">
                    <span>Summe EK</span>
                    <span className="font-mono">{formatCurrency(selectedMargin.total_purchase_cost)}</span>
                  </div>
                </div>
              </div>

              {/* Montagekosten */}
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2 text-sm text-muted-foreground">
                  <Wrench className="h-4 w-4" />
                  MONTAGEKOSTEN
                </h4>
                <div className="border rounded-lg divide-y">
                  <div className="flex items-center justify-between p-3">
                    <span>DC-Kosten (pro Modul × {selectedMargin.module_count})</span>
                    <span className="font-mono">{formatCurrency(selectedMargin.dc_installation_cost)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <span>AC-Kosten (Pauschalen)</span>
                    <span className="font-mono">{formatCurrency(selectedMargin.ac_installation_cost)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted font-medium">
                    <span>Summe Montage</span>
                    <span className="font-mono">{formatCurrency(selectedMargin.total_installation_cost)}</span>
                  </div>
                </div>
              </div>

              {/* Provision */}
              {selectedMargin.commission > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    VERTRIEBSPROVISION
                  </h4>
                  <div className="border rounded-lg">
                    <div className="flex items-center justify-between p-3 bg-muted font-medium">
                      <span>Provision (10% + Überverkauf - Rabatt)</span>
                      <span className="font-mono">{formatCurrency(selectedMargin.commission)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Gesamtkosten & Marge */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Gesamtkosten</span>
                  <span className="font-mono">{formatCurrency(selectedMargin.total_cost)}</span>
                </div>
                <div className={`flex items-center justify-between p-4 rounded-lg ${
                  selectedMargin.margin_amount >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <MarginIcon value={selectedMargin.margin_amount} />
                    <span className="font-semibold">Marge</span>
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-bold ${getMarginColorClass(selectedMargin.margin_amount)}`}>
                      {formatCurrency(selectedMargin.margin_amount)}
                    </div>
                    <div className="text-sm">
                      <MarginBadge percent={selectedMargin.margin_percent} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
